import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { evalRunForCriterion, useJudgementSource } from '../../hooks/useJudgementSource';
import {
  createReviewedOutput,
  fetchCriteria,
  fetchPolicy,
  fetchStylusJudgement,
  fetchStylusTranscriptions,
} from '../../services/api';
import { labelColors } from '../../theme';
import {
  type Criteria,
  type LabelType,
  type Policy,
  type StylusJudgement,
  type StylusTranscriptionSummary,
} from '../../types';
import ClassificationChip, { PURPLE_SX } from '../common/ClassificationChip';
import JudgementSourceSelector from '../common/JudgementSourceSelector';
import ReviewFilterEmptyState from '../common/ReviewFilterEmptyState';
import { outputReviewEmptyCopy } from '../common/reviewEmptyState';
import SearchableSelect from '../common/SearchableSelect';
import EssayContent from '../EssayContent';

type ReviewLabelType = 'positive' | 'negative' | 'both' | 'uncertain' | null;

type ReasonSource = 'ai' | 'human';

interface EntityLabel {
  type: ReviewLabelType;
  reason?: string;
  source: ReasonSource;
}

const OutputReview: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [labels, setLabels] = useState<EntityLabel[]>([]);
  const [editType, setEditType] = useState<ReviewLabelType | 'no attempt'>(null);
  const [editReason, setEditReason] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const [criteriaList, setCriteriaList] = useState<Criteria[]>([]);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState('');
  const [activePolicy, setActivePolicy] = useState<Policy | null>(null);
  const [loadingCriteria, setLoadingCriteria] = useState(true);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  const [transcriptionList, setTranscriptionList] = useState<StylusTranscriptionSummary[]>([]);
  const [selectedTranscriptionId, setSelectedTranscriptionId] = useState('');
  const [judgement, setJudgement] = useState<StylusJudgement | null>(null);
  const [loadingTranscriptions, setLoadingTranscriptions] = useState(false);
  const [loadingJudgement, setLoadingJudgement] = useState(false);
  const {
    source,
    setKind,
    evalRuns,
    productionFrom,
    productionTo,
    loadingEvalRuns,
    evalRunsError,
    usedProductionFallback,
    isReady,
  } = useJudgementSource();

  const judgementEntities = judgement?.entities ?? [];
  const entities = judgement
    ? judgementEntities.length > 0
      ? judgementEntities
      : judgement.sentences.map((sentence, index) => ({
          ...sentence,
          index,
          text: sentence.sentence,
        }))
    : [];
  const fragments = entities.map(entity => entity.text || entity.sentence);
  const selectedGranularity =
    judgement?.granularity ??
    criteriaList.find(criterion => criterion.id === selectedCriteriaId)?.granularity ??
    'sentence';
  const classificationHeading = `${selectedGranularity.charAt(0).toUpperCase()}${selectedGranularity.slice(1)} classifications`;

  const sortedCriteriaOptions = useMemo(
    () =>
      [...criteriaList]
        .filter(c => !c.isArchived || c.id === selectedCriteriaId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(c => ({ value: c.id, label: `${c.name} [${c.granularity}]` })),
    [criteriaList, selectedCriteriaId]
  );

  const transcriptionListOptions = useMemo(
    () => transcriptionList.map(t => ({ value: t.id, label: `${t.contentPreview}...` })),
    [transcriptionList]
  );

  const policyRequestIdRef = useRef(0);
  const transcriptionsRequestIdRef = useRef(0);
  const judgementRequestIdRef = useRef(0);

  const loadPolicy = useCallback((policyId: string) => {
    const requestId = ++policyRequestIdRef.current;
    setLoadingPolicy(true);
    fetchPolicy(policyId)
      .then(policy => {
        if (policyRequestIdRef.current === requestId) {
          setActivePolicy(policy);
          setLoadingPolicy(false);
        }
      })
      .catch(() => {
        if (policyRequestIdRef.current === requestId) {
          setActivePolicy(null);
          setLoadingPolicy(false);
        }
      });
  }, []);

  useEffect(() => {
    fetchCriteria().then(data => {
      setCriteriaList(data);
      setLoadingCriteria(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedCriteriaId) {
      policyRequestIdRef.current++;
      setActivePolicy(null);
      setTranscriptionList([]);
      setSelectedTranscriptionId('');
      setJudgement(null);
      setLabels([]);
      return;
    }
    const criteria = criteriaList.find(c => c.id === selectedCriteriaId);
    if (criteria?.policyId) {
      loadPolicy(criteria.policyId);
    } else {
      policyRequestIdRef.current++;
      setActivePolicy(null);
      setLoadingPolicy(false);
    }

    setLoadingTranscriptions(true);
    setSelectedTranscriptionId('');
    setJudgement(null);
    setLabels([]);
    setSelectedIndex(null);
    if (!isReady) {
      setTranscriptionList([]);
      setLoadingTranscriptions(false);
      return;
    }
    const reqId = ++transcriptionsRequestIdRef.current;
    fetchStylusTranscriptions(selectedCriteriaId, source)
      .then(data => {
        if (transcriptionsRequestIdRef.current !== reqId) return;
        setTranscriptionList(data);
        setLoadingTranscriptions(false);
        if (data.length > 0) {
          setSelectedTranscriptionId(data[0].id);
        }
      })
      .catch(() => {
        if (transcriptionsRequestIdRef.current !== reqId) return;
        setTranscriptionList([]);
        setLoadingTranscriptions(false);
      });
  }, [selectedCriteriaId, criteriaList, loadPolicy, isReady, source]);

  useEffect(() => {
    if (!selectedCriteriaId || !selectedTranscriptionId) {
      judgementRequestIdRef.current++;
      setJudgement(null);
      setLabels([]);
      return;
    }
    const reqId = ++judgementRequestIdRef.current;
    setLoadingJudgement(true);
    setSelectedIndex(null);
    fetchStylusJudgement(selectedCriteriaId, selectedTranscriptionId, source)
      .then(data => {
        if (judgementRequestIdRef.current !== reqId) return;
        setJudgement(data);
        const entityRows = data.entities ?? [];
        const responseEntities = entityRows.length > 0 ? entityRows : data.sentences;
        setLabels(
          responseEntities.map(entity => ({
            type: entity.label as ReviewLabelType,
            reason: entity.reason,
            source: 'ai' as ReasonSource,
          }))
        );
        setLoadingJudgement(false);
      })
      .catch(() => {
        if (judgementRequestIdRef.current !== reqId) return;
        setJudgement(null);
        setLabels([]);
        setLoadingJudgement(false);
      });
  }, [selectedCriteriaId, selectedTranscriptionId, source]);

  const selectedLabel = selectedIndex !== null ? labels[selectedIndex] : null;
  const currentLabelType = selectedLabel?.type ?? null;

  const getLabelTypeForFragment = (index: number): LabelType | null => {
    return labels[index]?.type as LabelType | null;
  };

  const handleFragmentClick = (_fragment: string, index: number) => {
    if (selectedIndex === index) {
      setSelectedIndex(null);
      setIsEditing(false);
    } else {
      setSelectedIndex(index);
      setIsEditing(false);
      setEditType(null);
      setEditReason('');
    }
  };

  const handleStartEdit = () => {
    if (selectedIndex === null) return;
    setEditType(null);
    setEditReason('');
    setIsEditing(true);
  };

  const handleSubmitEdit = async () => {
    if (selectedIndex === null || !editReason.trim() || !activePolicy) return;
    const resolvedType = editType === 'no attempt' ? null : editType;
    if (resolvedType === currentLabelType) return;
    const prevLabel = labels[selectedIndex];
    setLabels(prev => {
      const updated = [...prev];
      updated[selectedIndex] = { type: resolvedType, source: 'human', reason: editReason.trim() };
      return updated;
    });
    setIsEditing(false);
    setSelectedIndex(null);
    try {
      await createReviewedOutput({
        criteriaId: selectedCriteriaId,
        policyId: activePolicy.id,
        stylusTranscriptionId: selectedTranscriptionId,
        sentence: fragments[selectedIndex],
        originalClassification: currentLabelType ?? 'no attempt',
        correctedClassification: resolvedType ?? 'no attempt',
        reason: editReason.trim(),
      });
    } catch {
      setLabels(prev => {
        const updated = [...prev];
        updated[selectedIndex] = prevLabel;
        return updated;
      });
    }
  };

  return (
    <Box
      sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', mt: 4, px: 3 }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        Output Review
      </Typography>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper className="paper">
            <Box className="header-box">
              <Typography variant="h6">Labelling Policy</Typography>
            </Box>
            <Box sx={{ px: 2, pt: 1, pb: 1 }}>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Criterion</InputLabel>
                <SearchableSelect
                  value={selectedCriteriaId}
                  label="Criterion"
                  onChange={e => setSelectedCriteriaId(e.target.value as string)}
                  disabled={loadingCriteria}
                  options={sortedCriteriaOptions}
                />
              </FormControl>
              <JudgementSourceSelector
                source={source}
                evalRuns={evalRuns}
                productionFrom={productionFrom}
                productionTo={productionTo}
                loadingEvalRuns={loadingEvalRuns}
                evalRunsError={evalRunsError}
                usedProductionFallback={usedProductionFallback}
                selectedCriteriaId={selectedCriteriaId}
                onKindChange={setKind}
              />
            </Box>
            <Box className="content-box">
              <Box className="markdown-container">
                {loadingCriteria || loadingPolicy ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : activePolicy ? (
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{activePolicy.text}</ReactMarkdown>
                ) : (
                  <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                    {selectedCriteriaId
                      ? 'No policy found for this criterion'
                      : 'Select a criterion to view its policy'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper className="paper">
            <Box className="header-box">
              <Typography variant="h6">{classificationHeading}</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label="Positive" size="small" color="success" variant="outlined" />
                <Chip label="Negative" size="small" color="error" variant="outlined" />
                <Chip label="Both" size="small" variant="outlined" sx={PURPLE_SX} />
              </Box>
            </Box>

            {selectedCriteriaId && (
              <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transcription</InputLabel>
                  <SearchableSelect
                    value={selectedTranscriptionId}
                    label="Transcription"
                    onChange={e => setSelectedTranscriptionId(e.target.value as string)}
                    disabled={loadingTranscriptions}
                    options={transcriptionListOptions}
                  />
                </FormControl>
                {judgement?.policyId && activePolicy && judgement.policyId !== activePolicy.id && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This judgement was made using an older policy version
                  </Alert>
                )}
                {judgement && (judgement.excludedEntityCount ?? 0) > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {judgement.excludedEntityCount ?? 0}{' '}
                    {judgement.excludedEntityCount === 1 ? 'entity was' : 'entities were'} excluded
                    because they could not be resolved safely.
                  </Alert>
                )}
                {(judgement?.entityWarnings ?? []).map((warning, index) => (
                  <Alert key={`${warning}-${index}`} severity="warning" sx={{ mt: 1 }}>
                    {warning}
                  </Alert>
                ))}
              </Box>
            )}

            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, pl: 1 }}>
              {loadingTranscriptions || loadingJudgement ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : fragments.length > 0 ? (
                <EssayContent
                  fragments={fragments}
                  selectedFragmentIndex={selectedIndex}
                  handleFragmentClick={handleFragmentClick}
                  getLabelTypeForFragment={getLabelTypeForFragment}
                  granularity={selectedGranularity}
                />
              ) : !isReady ? (
                <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                  Select an Eval Run to load AI Judgements
                </Typography>
              ) : !selectedCriteriaId ? (
                <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
                  Select a criterion to view classifications
                </Typography>
              ) : (
                <ReviewFilterEmptyState
                  {...outputReviewEmptyCopy({
                    sourceKind: source.kind,
                    evalRunCoversCriterion: Boolean(
                      evalRunForCriterion(evalRuns, selectedCriteriaId)
                    ),
                  })}
                />
              )}
            </Box>

            {selectedIndex !== null && (
              <Box sx={{ pt: 1, mt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {!isEditing ? (
                  <>
                    {selectedLabel?.type && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: selectedLabel.reason ? 1 : 0,
                        }}
                      >
                        <Typography variant="subtitle2">Classification:</Typography>
                        <ClassificationChip value={selectedLabel.type} />
                      </Box>
                    )}
                    {selectedLabel?.reason && (
                      <Box
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor:
                            selectedLabel.source === 'human'
                              ? 'rgba(25, 118, 210, 0.06)'
                              : 'rgba(0, 0, 0, 0.03)',
                          borderLeft:
                            selectedLabel.source === 'human'
                              ? '3px solid #1976d2'
                              : '3px solid rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                        >
                          {selectedLabel.source === 'human' ? 'Human feedback' : 'AI reasoning'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedLabel.reason}
                        </Typography>
                      </Box>
                    )}
                    <Button variant="outlined" size="small" onClick={handleStartEdit}>
                      Change Classification
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      New Classification
                    </Typography>
                    <ToggleButtonGroup
                      value={editType}
                      exclusive
                      onChange={(_e, val) => {
                        if (val !== null) setEditType(val);
                      }}
                      size="small"
                      sx={{ mb: 1.5 }}
                    >
                      {(['positive', 'negative', 'both', 'uncertain'] as const).map(type => (
                        <ToggleButton
                          key={type}
                          value={type}
                          disabled={currentLabelType === type}
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: labelColors[type].main,
                              borderColor: labelColors[type].border,
                            },
                          }}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </ToggleButton>
                      ))}
                      <ToggleButton value="no attempt" disabled={currentLabelType === null}>
                        No attempt
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Reason for change..."
                      value={editReason}
                      onChange={e => setEditReason(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          !editReason.trim() ||
                          (editType === 'no attempt' ? null : editType) === currentLabelType
                        }
                        onClick={handleSubmitEdit}
                      >
                        Submit
                      </Button>
                      <Button variant="text" size="small" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OutputReview;
